import React, { createContext, useContext, useMemo } from 'react'
import useUrlSearchParams from '../hooks/useUrlSearchParams'
import useGetLocalStorage from '../hooks/useGetLocalStorage'
import localStorageIds from '../utils/constants/localStorageIds'
import ORGANISATION_TYPES from '../utils/constants/OrganisationTypes'

// Which org's CMS the CMS screens show, and whether the caller may change it. Without a
// provider they show the selected org's own CMS, editable, unless the URL carries a paired
// federation (fedOrg + competition): a club browsing the federation competition its team is
// paired into. Those screens show the federation, read-only, under their own localStorage
// namespace so the club's selections survive. ?org stays the club: it is the selected org.
const CmsScopeContext = createContext(null)

export const CmsScopeProvider = ({ orgId, orgType, readOnly = false, competitionId = null, storageNamespace = null, children }) => {
  const value = useMemo(
    () => ({ orgId, orgType, readOnly, competitionId, storageNamespace }),
    [orgId, orgType, readOnly, competitionId, storageNamespace],
  )
  return <CmsScopeContext.Provider value={value}>{children}</CmsScopeContext.Provider>
}

export const useCmsScope = () => {
  const scope = useContext(CmsScopeContext)
  const { org, fedOrg, competition } = useUrlSearchParams(['org', 'fedOrg', 'competition']) || {}
  const localStorageValue = useGetLocalStorage()

  const paired = fedOrg && competition && {
    orgId: fedOrg,
    orgType: ORGANISATION_TYPES.ORG_FEDERATION,
    readOnly: true,
    competitionId: competition,
    storageNamespace: `paired:${competition}`,
  }
  const resolved = scope || paired || {
    orgId: org,
    orgType: localStorageValue?.[localStorageIds.SELECTED_ORG]?.type,
    readOnly: false,
    competitionId: null,
    storageNamespace: null,
  }
  const storageId = (id) => (resolved.storageNamespace ? `${resolved.storageNamespace}:${id}` : id)
  return { ...resolved, storageId }
}
